import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import type { GridFieldType } from '@/features/Inventory/ObjectTypePassportGrid/model/types'

/** Hop edge for linked resolution; `field_id` may be nanoid string (matches `NestedColumnSegment` usage in maps). */
export type LinkedHopSegment = {
  object_type_id: number
  field_id: number | string
  /**
   * Third bracket component: expected `object_type_id` on the loaded dictionary row
   * (`GET …/object-types/{id}`). `0` when unknown (non-`ref:` hop sources).
   */
  expected_object_type_id: number
}

/**
 * Parses `list_types` hop chain from `ref:[…]->[…]#f:<leaf>` grid ids (palette «Справочники»).
 */
export function parseRefHopChainFromGridId(gridId: string): LinkedHopSegment[] {
  const m = /^ref:(.+)#f:/.exec(String(gridId))
  if (!m?.[1]) return []
  const chain = m[1]
  const segments: LinkedHopSegment[] = []
  const hopRe = /\[(\d+):([^:]+):(\d+)\]/g
  let hm: RegExpExecArray | null
  while ((hm = hopRe.exec(chain)) !== null) {
    const edge = hm[2]
    const n = Number(edge)
    const expected = Number(hm[3])
    segments.push({
      object_type_id: Number(hm[1]),
      field_id: Number.isFinite(n) && String(n) === String(edge) ? n : edge,
      expected_object_type_id: Number.isFinite(expected) ? expected : 0,
    })
  }
  return segments
}

/** `parent-acc-<ot>-<edge>-root::<leaf>::` — single hop encoded in legacy id. */
function parseParentAccordionHopSegments(
  gridId: string,
  catalogTypeId: number,
): LinkedHopSegment[] | null {
  const m = /^parent-acc-\d+-([A-Za-z0-9]+)-root::([A-Za-z0-9]+)::/.exec(String(gridId))
  if (!m?.[1]) return null
  const edge = m[1]
  const n = Number(edge)
  return [
    {
      object_type_id: catalogTypeId,
      field_id: Number.isFinite(n) && String(n) === String(edge) ? n : edge,
      expected_object_type_id: 0,
    },
  ]
}

function inferSingleHopFromCatalog(
  field: ObjectInventoryField,
  catalogTypeId: number,
  catalogFields: ObjectInventoryField[],
): LinkedHopSegment[] {
  const targetOt = Number(field.object_type)
  if (!Number.isFinite(targetOt) || targetOt <= 0) return []
  for (const f of catalogFields) {
    if (String(f.type ?? '').trim() !== 'list_types') continue
    if (Number(f.object_type) === targetOt) {
      const id = f.id
      const num = Number(id)
      const edgeFieldId =
        Number.isFinite(num) && String(num) === String(id) ? num : id
      return [
        {
          object_type_id: catalogTypeId,
          field_id: edgeFieldId,
          expected_object_type_id: targetOt,
        },
      ]
    }
  }
  return []
}

/**
 * Resolves `list_types` hop segments for linked value resolution (same sources as builder palette).
 */
export function parseHopSegmentsForLinkedResolution(
  item: GridFieldType,
  catalogTypeId: number,
  catalogFields: ObjectInventoryField[],
  fieldResolved?: ObjectInventoryField,
): LinkedHopSegment[] {
  const segs = item.nested_binding?.segments
  if (Array.isArray(segs) && segs.length > 0) {
    return segs.map((s) => ({
      object_type_id: Number(s.object_type_id),
      field_id: (() => {
        const id = s.field_id
        const n = Number(id)
        return Number.isFinite(n) && String(n) === String(id) ? n : id
      })(),
      expected_object_type_id: 0,
    }))
  }

  const fromRef = parseRefHopChainFromGridId(String(item.id))
  if (fromRef.length) return fromRef

  const fromAcc = parseParentAccordionHopSegments(String(item.id), catalogTypeId)
  if (fromAcc?.length) return fromAcc

  if (fieldResolved) {
    return inferSingleHopFromCatalog(fieldResolved, catalogTypeId, catalogFields)
  }

  return []
}

export function parseLeafFieldIdForLinkedResolution(
  item: GridFieldType,
  fieldResolved?: ObjectInventoryField,
): string | number | null {
  const sid = String(item.id)
  const isRefComposite = sid.startsWith('ref:')
  const hashIdx = sid.lastIndexOf('#f:')
  const fromHash = hashIdx >= 0 ? sid.slice(hashIdx + 3) : null

  if (fromHash && (fromHash !== 'key' || isRefComposite)) {
    return fromHash
  }
  const nestedLeaf = item.nested_binding?.leaf_field_id
  if (nestedLeaf != null && (String(nestedLeaf) !== 'key' || isRefComposite)) {
    return nestedLeaf
  }

  const accLeaf = /^parent-acc-\d+-[A-Za-z0-9]+-root::([A-Za-z0-9]+)::/.exec(sid)
  if (accLeaf?.[1] && accLeaf[1] !== 'key') return accLeaf[1]

  const prefixed = /^(?:parent|element):([^:]+):.+$/.exec(sid)
  if (prefixed?.[1]?.trim()) return prefixed[1].trim()

  if (fieldResolved?.id != null) return fieldResolved.id
  return null
}

/** Parses the leaf inventory field id from `element:{linkFieldId}#f:{fieldId}` grid ids. */
export function parseLeafFromElementId(gridId: string): string | null {
  const m = /^element:[^#]+#f:(.+)$/.exec(String(gridId))
  return m?.[1] ?? null
}

/** Получает id поля связи из `element:{linkFieldId}#f:{fieldId}`. */
export const parseLinkFieldIdFromElementId = (gridId: string): string | null => {
  const m = /^element:([^#]+)#f:.+$/.exec(String(gridId))
  return m?.[1] ?? null
}

/**
 * Reads `default_value` from dictionary `fields[]` for the first entry matching `fieldId`.
 * Logs a warning when multiple entries share the same id.
 */
export function findFirstFieldDefaultValueById(
  fields: Array<{ id: string | number; default_value?: unknown }> | null | undefined,
  fieldId: string | number,
): unknown {
  if (!Array.isArray(fields)) return undefined
  const key = String(fieldId)
  let firstIdx = -1
  let count = 0
  for (let i = 0; i < fields.length; i++) {
    if (String(fields[i]?.id) !== key) continue
    count += 1
    if (firstIdx < 0) firstIdx = i
  }
  if (count === 0) return undefined
  if (count > 1) {
    console.warn(
      `[passport-ref-dict] multiple fields with id "${key}" (${count} entries), using first`,
    )
  }
  return fields[firstIdx]?.default_value
}
